import { Entity } from 'src/app/core/data/schema/entity';
import { PageState } from 'src/app/core/base-page'; 
import { Utilities } from 'src/app/core/helper/util';
import { SafeResourceUrl } from '@angular/platform-browser';

import { ServiceHelper } from 'src/app/core/helper/services.helper';

/**
 * Just pulling in the mos rudimentary page definition into BB client for 
 * facilitating quick create of event, possibly sponsors
 */
export enum PageType {
    // the first bunch of types do NOT map to a specific entity,
    // so all the info to render exists on the page and its own blocks only.
    Page = 1,           // static page - comes from just the Pages table 
    Post = 2,           // news article - comes from just the Pages table 

    // Each Category will have it's own page, routed to under the corresponding
    // CategoryGroup, further below
    MarketplaceCategory = 3, 
    EventCategory = 4,   
    AuctionCategory = 5,
    SponsorCategory = 6,
    FilterCategory = 7,
    ResourceCategory = 29,
    
    // These are CategoryGroup pages, of which there really only exists one page each
    // excpet for the Filter Home, since admins can create several of these 
    MarketplaceHome = 8,
    EventsHome = 9,
    SponsorsHome = 10,
    AuctionHome = 11,  
    FilterHome= 12, // this one will 
    ResourcesHome = 13,

    // These page types are all linked to entities
    Resource = 14,      // comes from CategoryGroups Table 
    Donation = 15,      // comes from Products table 
    Event = 16,         // comes from Events table
    ForumCategory = 17, // comes from ForumCategoires table (when we implement)
    FundraisingCampaign = 18,// comes from FundraisingCampaigns table
    Membership = 19,    // comes from Memberships table TODO
    Newsletter = 20,    // comes fron Newsletters table
    Onboarding = 21,    // comes from Onboarding table
    Product = 22,       // comes from Products table
    Special = 23,       // comes from Specials table
    Sponsor = 24,       // comes from Products table
    Survey = 25,        // comes from Products table
    Raffle = 26,        // comes from Raffles table
    Auction = 27,        // comes from Auctions table
    Directory = 28,     // comes from CategoryGroups Table 
    // 29 is resource category above

    
    // PSEUDO PAGES
    // these are pages that are actually appended or prepended to other pages,
    // and are never displayed on their own. Like they shouldn't even really
    // have a slug. 
    
    // footer to be appended at bottom of every page in site
    Footer = 100,

    // affiliate pages are used for customizing donation pages, or really any page 
    // that can have sharing attributed back to a user
    Affiliate = 101, 
}


/**
 * It's possible that a page is LIVE but openAt is in the future
 * or closeAt is in the past, in which case we should show a different
 * status, perhaps Future and Past? though we wouldn't update DB 
 * status to that value..
 */
export enum PageStatus {
    Draft = 1,
    Live = 2,
    Archived = 3
}

export enum MediaDisplay {
    NoMedia = 1,
    HalfWidth = 2,
    FullWidth = 3,
    CustomBlocksOnly = 4,
}

export class Page extends Entity {

    protected static className = 'Page';
    protected static idField = 'pageId';
    
    pageId: number = 0;
    slug: string; // alternate url path, so slug needs to be unique. If not set, use id.
    communityId: number;
    auctionId: number;
    pgType: PageType;
    pgItemId: number; // the primary key of item (event, category, product, sponsor, etc) that this page belongs to
    pgStatus: PageStatus = PageStatus.Live;
    title: string;
    shortDesc: string;
    featureImgPath: string;
    body: string;
    imagePaths: string;
    notFiltered: boolean;
    highlightColor: string;
    
    // overloading this to also be used for external url if page is 
    // specified as external link page wrapper
    videoUrl: string;

    useVid: boolean = false;

    
    
    deleted : boolean = false;


    sanitizedFeatureImgUrl: SafeResourceUrl;

    
    // using getter here cause i don't want this to impact change checking
    get sanitizedBody() {
        if ( this.body != null ) {return ServiceHelper.sanitizer.bypassSecurityTrustHtml(this.body)} 
        else{return null}
    };

    set sanitizedBody(value: any) {
        // do nothing, this just satisfies the setter requirement when deserializing
    }

    constructor(page?: Partial<Page>) {
        super();
        Object.assign(this, page);
    }

 

    hasChanges(){
        return Page.hasChanges(Page);
    }
    revert(){
        return Page.revert(Page);
    }

    // overriding save function so we for sure always set dates back to UTC
    override async save(busyState: PageState = null, controller: string | undefined = this.controller, endpoint: string = '') : Promise<boolean> {

        if ( await super.save(busyState, controller, endpoint) ){
            return true;''
        }
        return false;
    }


    public async copyPage(busyState?: PageState) {
        busyState.busy = true;
        const copy = new Page(await Entity.factory.post("Copy", { name: this.title + ' - Copy'} as any, 'pages', `copy/${this.pageId}`));
        busyState.busy = false;
        return copy;
    }

    async addImg(base64Imgs: string) {
        if ( Utilities.stringEmpty(this.imagePaths) ) {
            this.imagePaths = base64Imgs;
        } else {
            this.imagePaths = this.imagePaths + '|' + base64Imgs;
        }
    }

    // this function is called when someone clicks on an item preview. It will check the item type
    // on the page and open the route accordingly
    // viewItem(){
    //     RoutingHelper.routeTo(this.pgType, this.slug, this.pgItemId );
    // }

    // ----------------------------------------------------
    // Service-like features from here on down
    // ----------------------------------------------------

    public static async getBySlug(slug: string) {
        return await Page.getObject(Page, 'pages', `forslug/${slug}`, true);
    }

}

